/**
 * Utility for optimizing images before upload
 * Converts images to WebP format, resizes and compresses
 */

import { logger } from './logger'

export interface ImageOptimizationOptions {
  /** Target format (default: 'image/webp') */
  format?: string
  /** Quality from 0 to 1 (default: 0.75) */
  quality?: number
  /** Maximum width in pixels (default: 1600) */
  maxWidth?: number
  /** Maximum height in pixels (default: 1600) */
  maxHeight?: number
  /** Preserve aspect ratio (default: true) */
  preserveAspectRatio?: boolean
}

const DEFAULT_OPTIONS: Required<ImageOptimizationOptions> = {
  format: 'image/webp',
  quality: 0.75,
  maxWidth: 1600,
  maxHeight: 1600,
  preserveAspectRatio: true,
}

/**
 * Optimizes an image file for web display
 * Converts to WebP, resizes and compresses
 */
export async function optimizeImage(
  file: File,
  options: ImageOptimizationOptions = {}
): Promise<File> {
  const opts = { ...DEFAULT_OPTIONS, ...options }
  
  // If file is not an image, return original
  if (!file.type.startsWith('image/')) {
    logger.warn('File is not an image, skipping optimization:', file.type)
    return file
  }
  
  // Skip optimization for very small images (icons, tiny thumbnails)
  if (file.size < 50 * 1024) { // 50KB
    logger.log('Image is very small, skipping optimization:', file.name, file.size)
    return file
  }
  
  // Skip if already WebP and small enough (< 500KB and dimensions likely small)
  if (file.type === 'image/webp' && file.size < 500 * 1024) {
    logger.log('Image is already WebP and relatively small, skipping optimization:', file.name, file.size)
    return file
  }
  
  // If file is already WebP and within size limits, consider returning as-is
  // (but we still want to check dimensions)
  
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    
    reader.onload = async (e) => {
      try {
        const img = new Image()
        
        img.onload = async () => {
          try {
            // Calculate new dimensions
            let width = img.width
            let height = img.height
            
            if (width > opts.maxWidth || height > opts.maxHeight) {
              if (opts.preserveAspectRatio) {
                const ratio = Math.min(
                  opts.maxWidth / width,
                  opts.maxHeight / height
                )
                width = Math.floor(width * ratio)
                height = Math.floor(height * ratio)
              } else {
                width = Math.min(width, opts.maxWidth)
                height = Math.min(height, opts.maxHeight)
              }
            }
            
            // Create canvas for resizing and conversion
            const canvas = document.createElement('canvas')
            canvas.width = width
            canvas.height = height
            
            const ctx = canvas.getContext('2d')
            if (!ctx) {
              reject(new Error('Canvas context not available'))
              return
            }
            
            // Draw image with new dimensions
            ctx.drawImage(img, 0, 0, width, height)
            
            // Convert to Blob with target format and quality
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error('Failed to create image blob'))
                  return
                }
                
                // Create new File with optimized image
                const optimizedFile = new File(
                  [blob],
                  `${file.name.replace(/\.[^/.]+$/, '')}.webp`,
                  { type: opts.format }
                )
                
                 logger.log('Image optimized:', {
                  original: {
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    dimensions: `${img.width}x${img.height}`
                  },
                  optimized: {
                    name: optimizedFile.name,
                    size: optimizedFile.size,
                    type: optimizedFile.type,
                    dimensions: `${width}x${height}`,
                    quality: opts.quality,
                    compression: `${Math.round((1 - (optimizedFile.size / file.size)) * 100)}%`
                  }
                })
                
                resolve(optimizedFile)
              },
              opts.format,
              opts.quality
            )
          } catch (error) {
            reject(new Error(`Failed to optimize image: ${error}`))
          }
        }
        
        img.onerror = () => {
          reject(new Error('Failed to load image for optimization'))
        }
        
        img.src = e.target?.result as string
      } catch (error) {
        reject(new Error(`Failed to process image: ${error}`))
      }
    }
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'))
    }
    
    reader.readAsDataURL(file)
  })
}

/**
 * Quick optimization for non-critical images (avatars, thumbnails)
 */
export async function optimizeImageQuick(file: File): Promise<File> {
  return optimizeImage(file, {
    format: 'image/webp',
    quality: 0.6,
    maxWidth: 800,
    maxHeight: 800,
  })
}

/**
 * High quality optimization for important images (listing photos)
 */
export async function optimizeImageHighQuality(file: File): Promise<File> {
  return optimizeImage(file, {
    format: 'image/webp',
    quality: 0.85,
    maxWidth: 1600,
    maxHeight: 1600,
  })
}

/**
 * Check if image needs optimization (large dimensions or wrong format)
 */
export function needsOptimization(file: File): boolean {
  if (!file.type.startsWith('image/')) return false
  
  // Check if it's not WebP
  if (file.type !== 'image/webp') return true
  
  // We'll check dimensions by loading the image
  // For now assume it might need optimization if > 1MB
  return file.size > 1024 * 1024 // 1MB
}